<script async setup lang="ts">
import { ratings } from '@/composables/filterpage.helpers'
import { CULTURE } from '@/composables/helpers'
import type { IAccoItem } from '@/composables/interfaces/apiInterfaces'
import t from '@/i18n/translations'
import { filtersData, filterStore, ratingsFilterSelected, ratingsFilterSelectedIgnoreUpdates } from '@/store/filter.store'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import RadioButton from 'primevue/radiobutton'
import { onMounted } from 'vue'

const props = defineProps<{
  hideMap: boolean
  accos: Array<IAccoItem>
}>()

const emit = defineEmits(['show-map'])

function disableOption(id: number): void {
  if (filterStore.value.themeId === id) {
    filterStore.value.themeId = 0
  }
}

function onOpenMap(): void {
  emit('show-map', true)
}

onMounted(() => {
  if (filterStore.value.ratingFrom && filterStore.value.ratingTo) {
    ratingsFilterSelectedIgnoreUpdates(() => {
      ratingsFilterSelected.value = Array.from(
        { length: filterStore.value.ratingTo - filterStore.value.ratingFrom + 1 },
        (v, i) => filterStore.value.ratingFrom + i
      )
    })
  }
})
</script>

<template>
  <div class="grid grid-cols-1 justify-between gap-4">
    <div id="acco-types" class="flex flex-col gap-2" v-if="filtersData!.accotypes">
      <div class="mb-2 border-b pb-1 font-bold">
        {{ t(`${CULTURE}.filter.accomodation`) }}
      </div>
      <div class="flex flex-col gap-2">
        <div class="overwrite-primary-contrast flex items-center" v-for="accommodation in filtersData!.accotypes" :key="accommodation.id">
          <label :for="accommodation.id + 'ac'" class="ml-2 flex cursor-pointer items-center gap-2 text-lg lg:text-base">
            <Checkbox v-model="filterStore.accotypeIds" name="accotypes" :value="accommodation.id" :input-id="accommodation.id + 'ac'" />
            {{ accommodation.name }}
          </label>
        </div>
      </div>
    </div>
    <div id="acco-rating" v-if="ratings">
      <div class="mb-2 border-b pb-1 font-bold">
        {{ t(`${CULTURE}.filter.accorating`) }}
      </div>
      <div class="flex flex-col gap-2">
        <div class="overwrite-primary-contrast flex items-center" v-for="rating in ratings()" :key="rating.value">
          <label :for="rating.value.toString() + 'rg'" class="ml-2 flex cursor-pointer items-center gap-2 text-lg lg:text-base">
            <Checkbox v-model="ratingsFilterSelected" name="accotypes" :value="rating.value" :input-id="rating.value + 'rg'" />
            {{ rating.label }}
          </label>
        </div>
      </div>
    </div>
    <div id="boardings" class="flex flex-col gap-2" v-if="filtersData!.boardings">
      <div class="mb-2 border-b pb-1 font-bold">
        {{ t(`${CULTURE}.filter.boardings`) }}
      </div>
      <div class="overwrite-primary-contrast flex items-center">
        <label :for="-1 + 'b'" class="ml-2 flex cursor-pointer items-center gap-2 text-lg lg:text-base">
          <RadioButton v-model="filterStore.boardingIds" name="boarding" :value="new Array()" :input-id="-1 + 'b'" />
          {{ t(`${CULTURE}.filter.boardingAny`) }}
        </label>
      </div>

      <div class="overwrite-primary-contrast flex items-center" v-for="boarding in filtersData!.boardings" :key="boarding.id">
        <label :for="boarding.id + 'b'" class="ml-2 flex cursor-pointer items-center gap-2 text-lg lg:text-base">
          <RadioButton v-model="filterStore.boardingIds" name="boarding" :value="[boarding.id]" :input-id="boarding.id + 'b'" />
          {{ boarding.name }}
        </label>
      </div>
    </div>
    <div id="amenities" class="flex flex-col gap-2" v-if="filtersData!.amenities">
      <div class="mb-2 border-b pb-1 font-bold">
        {{ t(`${CULTURE}.filter.amenities`) }}
      </div>
      <div class="overwrite-primary-contrast flex items-center" v-for="amenity in filtersData!.amenities" :key="amenity.id">
        <label :for="amenity.id + 'am'" class="ml-2 flex cursor-pointer items-center gap-2 text-lg lg:text-base">
          <Checkbox v-model="filterStore.amenityIds" name="amenity" :value="amenity.id" :input-id="amenity.id + 'am'" />
          <img class="w-7" :src="amenity.imagePath" />
          {{ amenity.name }}
        </label>
      </div>
    </div>
    <div id="themes" class="flex flex-col gap-2" v-if="filtersData!.themes">
      <div class="mb-2 border-b pb-1 font-bold">
        {{ t(`${CULTURE}.filter.themes`) }}
      </div>
      <div class="overwrite-primary-contrast flex items-center" v-for="theme in filtersData!.themes" :key="theme.id">
        <label :for="theme.id + 't'" class="ml-2 flex cursor-pointer items-center gap-2 text-lg lg:text-base">
          <RadioButton v-model="filterStore.themeId" name="theme" :value="theme.id" :input-id="theme.id + 't'" @click="disableOption(theme.id)" />
          {{ theme.name }}
        </label>
      </div>
    </div>
    <div id="map" class="relative mt-4 hidden items-center sm:flex" v-if="false">
      <Button size="small" class="pui-filterButton w-full" @click="onOpenMap">
        <div class="flex items-center gap-1">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-4">
            <path
              fill-rule="evenodd"
              d="m11.54 22.351.07.04.028.016a.76.76 0 0 0 .723 0l.028-.015.071-.041a16.975 16.975 0 0 0 1.144-.742 19.58 19.58 0 0 0 2.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 0 0-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 0 0 2.682 2.282 16.975 16.975 0 0 0 1.145.742ZM12 13.5a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
              clip-rule="evenodd"
            ></path>
          </svg>
          <span class="text-[var(--p-text-color)]">{{ t(`${CULTURE}.filter.map.show`) }} </span>
        </div>
      </Button>
    </div>
  </div>
</template>
